import {$getNodeByKey, $getRoot, LexicalEditor} from 'lexical';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';
import {EnhancedHeadingType} from './EnhancedHeadingPlugin';

/**
 * Registry to map heading IDs to their current node keys
 * This allows us to find nodes by heading ID even when node keys change
 */
const headingIdToNodeKeyMap = new Map<string, string>();

/**
 * Store the current editor role (template or document mode)
 * This is set by the EnhancedHeadingPlugin and used by nodes during creation
 */
let currentHeadingType: EnhancedHeadingType;
let currentEditorRole: 'template' | 'document' = 'document';

export function setHeadingType(type: EnhancedHeadingType): void {
  currentHeadingType = type;
}

export function setEditorRole(role: 'template' | 'document'): void {
  currentEditorRole = role;
}

export function isEditable(): boolean {
  return currentEditorRole === 'template' && currentHeadingType === 'enabled';
}

export function shouldBlockHeadingEdits(): boolean {
  return currentHeadingType !== undefined && !isEditable();
}

/**
 * Register a heading node's ID and key mapping
 */
export function registerHeadingNode(headingId: string, nodeKey: string): void {
  headingIdToNodeKeyMap.set(headingId, nodeKey);
}

/**
 * Unregister a heading node's ID and key mapping
 */
export function unregisterHeadingNode(headingId: string): void {
  headingIdToNodeKeyMap.delete(headingId);
}

function findHeadingNodeInRoot(headingId: string): EnhancedHeadingNode | null {
  let foundNode: EnhancedHeadingNode | null = null;

  const findHeadings = (node: any) => {
    if (foundNode) return;

    if (node instanceof EnhancedHeadingNode && node.getHeadingId() === headingId) {
      foundNode = node;
      return;
    }

    if (node.getChildren) {
      node.getChildren().forEach(findHeadings);
    }
  };

  findHeadings($getRoot());
  return foundNode;
}

/**
 * Find a heading node by its heading ID
 * This uses the registry to map headingId to nodeKey, then gets the node
 */
export function findHeadingNodeByHeadingId(
  editor: LexicalEditor,
  headingId: string
): EnhancedHeadingNode | null {
  let foundNode: EnhancedHeadingNode | null = null;
  const nodeKey = headingIdToNodeKeyMap.get(headingId);

  editor.getEditorState().read(() => {
    if (nodeKey) {
      const node = $getNodeByKey(nodeKey);
      if (node && node instanceof EnhancedHeadingNode && node.getHeadingId() === headingId) {
        foundNode = node;
      }
    }

    // Version preview editors can refresh the shared registry with their own node keys.
    // If that happens, recover by scanning the root editor before treating the heading as missing
    if (!foundNode) {
      foundNode = findHeadingNodeInRoot(headingId);
      if (foundNode) {
        headingIdToNodeKeyMap.set(headingId, foundNode.getKey());
      }
    }
  });

  return foundNode;
}

/**
 * Update the registry by scanning all heading nodes in the editor
 * This should be called when the editor state changes significantly
 */
export function updateHeadingNodeRegistry(editor: LexicalEditor): void {
  editor.getEditorState().read(() => {
    const root = $getRoot();

    // Clear existing registry
    headingIdToNodeKeyMap.clear();

    // Recursively find all heading nodes
    const findHeadings = (node: any) => {
      if (node instanceof EnhancedHeadingNode) {
        const headingId = node.getHeadingId();
        const nodeKey = node.getKey();
        headingIdToNodeKeyMap.set(headingId, nodeKey);
      }

      // Recursively check children
      if (node.getChildren) {
        node.getChildren().forEach(findHeadings);
      }
    };

    findHeadings(root);
  });
}
